import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import type { PaymentState } from "@/lib/extras-payment";

export interface ProjectExtra {
  id: string;
  project_id: string;
  name: string | null;
  full_name: string | null;
  /** VIB-1387: rolle i produksjonen (f.eks. "Statist", "Location-eier"). Settes av PL, ikke statisten selv. */
  role: string | null;
  email: string | null;
  phone_number: string | null;
  address: string | null;
  personal_number: string | null;
  bank_account: string | null;
  /** @deprecated VIB-572: erstattet av budget_line_id. Beholdes for legacy-data. */
  payment_type: string | null;
  /**
   * VIB-2092: utbetalingsflyt. draft → ready_for_payment (PL melder klar) →
   * paid (DL betaler). Erstatter den frie `payment_status`-teksten, som er
   * beholdt urørt for legacy Monday-data.
   */
  payment_state: PaymentState;
  ready_for_payment_at: string | null;
  ready_for_payment_by: string | null;
  paid_at: string | null;
  paid_by: string | null;
  budget_line_id: string | null;
  agreed_amount: number | null;
  contract_url: string | null;
  payment_status: string | null;
  invoice_id: string | null;
  /** VIB-675: tidspunkt for siste suksessfulle skjema-sending. NULL = aldri sendt. */
  extra_form_sent_at: string | null;
  created_at: string;
  deleted_at: string | null;
}

export function useProjectExtras(projectId: string | undefined) {
  return useQuery({
    queryKey: ["project-extras", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_extras")
        .select("*")
        .eq("project_id", projectId!)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as ProjectExtra[];
    },
  });
}

export function useCreateExtra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      project_id: string;
      full_name: string;
      role?: string;
      personal_number?: string;
      agreed_amount?: number;
      budget_line_id?: string | null;
      email?: string;
      phone_number?: string;
      bank_account?: string;
      /** VIB-675: opt-in for å sende skjema automatisk ved opprettelse.
       *  Default false — PL kaller useSendExtraForm manuelt eller batch-sender. */
      autoSendForm?: boolean;
    }) => {
      // Strip ut autoSendForm før insert (er ikke et DB-felt).
      const { autoSendForm: _autoSendForm, ...row } = input;
      const { data, error } = await supabase
        .from("project_extras")
        .insert(row)
        .select("id, project_id")
        .single();
      if (error) throw error;

      // Behold backwards-compat-pathen for callsites som eksplisitt opt-in til
      // auto-send. Hvis send feiler avbryter vi IKKE opprettelsen — extra-raden
      // er allerede lagret. Erik kan retry via per-rad-knapp.
      if (input.autoSendForm && data && input.email) {
        try {
          await supabase.functions.invoke("send-extra-form", {
            body: { extra_id: data.id, user_id: "" },
          });
        } catch (sendErr) {
          // Logg, men ikke kast — opprettelsen var en suksess.
          console.warn("autoSendForm feilet etter create:", sendErr);
        }
      }

      return data;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "project_extras", { projectId: vars.project_id });
      toast.success("Ekstern medarbeider opprettet");
    },
    onError: () => {
      toast.error("Noe gikk galt");
    },
  });
}

export function useUpdateExtra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; project_id: string; updates: Partial<ProjectExtra> }) => {
      const { error } = await supabase.from("project_extras").update(input.updates).eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "project_extras", { projectId: vars.project_id });
      toast.success("Ekstern medarbeider oppdatert");
    },
    onError: () => {
      toast.error("Noe gikk galt");
    },
  });
}

export function useSendExtraForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { extra_id: string; user_id: string; project_id?: string }) => {
      const { data, error } = await supabase.functions.invoke("send-extra-form", {
        body: { extra_id: input.extra_id, user_id: input.user_id },
      });
      if (error) throw error;

      // VIB-675: marker raden som "sendt" så UI kan vise dato + status.
      // Edge-function håndterer token + e-post; vi setter timestamp her for
      // å unngå å endre edge-funksjonen (gjenbrukes som-er).
      const sentAt = new Date().toISOString();
      const { error: upErr } = await supabase
        .from("project_extras")
        .update({ extra_form_sent_at: sentAt })
        .eq("id", input.extra_id);
      if (upErr) {
        // Ikke kast — e-posten er sendt. Bare logg så vi ser at status-sync feilet.
        console.warn("Kunne ikke oppdatere extra_form_sent_at:", upErr);
      }

      return data as { success: boolean; message: string; expires_at: string };
    },
    onSuccess: async (_d, vars) => {
      if (vars.project_id) {
        await invalidateFor(qc, "project_extras", { projectId: vars.project_id });
      }
      toast.success(_d.message ?? "Skjema sendt");
    },
    onError: (err: Error) => {
      toast.error(err?.message ?? "Kunne ikke sende skjema");
    },
  });
}

export function useDeleteExtra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; project_id: string }) => {
      const { error } = await supabase
        .from("project_extras")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "project_extras", { projectId: vars.project_id });
      toast.success("Ekstern medarbeider slettet");
    },
    onError: () => {
      toast.error("Noe gikk galt");
    },
  });
}
